import { Cell, Row, Table, flexRender } from "@tanstack/react-table";
import { Text, TextProps, XStack, YStack } from "tamagui";

/**
 * Phone layout for a tanstack table: one card per row, one label/value line
 * per visible column. Built from the desktop table's own column defs and row
 * model, so a column, sort or filter added there shows up here too.
 * `titleColumn` renders that column's cell as the card heading instead of a
 * labelled line; `hideColumns` drops columns that make no sense on a card.
 * Every cell is wrapped in app text styling, as the desktop tables do, except
 * `rawColumns`: components that lay themselves out (pills, install cards,
 * action buttons). flexRender returns an element even for a cell that
 * returns a plain string, so the wrap can't be decided from the value.
 * `skipEmpty` drops lines whose value is missing instead of showing a dash;
 * `rows` renders a subset of the row model (default: all of it).
 */
const isEmpty = (value: unknown) =>
  value === undefined || value === null || value === "";

export function TableCards<T>({
  table,
  titleColumn,
  hideColumns = [],
  rawColumns = ["status", "version", "actions"],
  skipEmpty = false,
  rows = table.getRowModel().rows,
  onRowPress,
}: {
  table: Table<T>;
  titleColumn?: string;
  hideColumns?: string[];
  rawColumns?: string[];
  skipEmpty?: boolean;
  rows?: Row<T>[];
  onRowPress?: (row: Row<T>) => void;
}) {
  const headers = new Map(
    table.getFlatHeaders().map((header) => [header.column.id, header]),
  );
  const renderCell = (cell: Cell<T, unknown>, textProps?: TextProps) => {
    const node = flexRender(cell.column.columnDef.cell, cell.getContext());
    return rawColumns.includes(cell.column.id) ? (
      node
    ) : (
      <Text {...textProps}>{node}</Text>
    );
  };

  return (
    <YStack gap="$3">
      {rows.map((row) => {
        const cells = row
          .getVisibleCells()
          .filter((cell) => !hideColumns.includes(cell.column.id))
          .filter((cell) => !skipEmpty || !isEmpty(cell.getValue()));
        const title = cells.find((cell) => cell.column.id === titleColumn);

        return (
          <YStack
            key={row.id}
            tag="article"
            gap="$2.5"
            padding="$3.5"
            borderRadius="$6"
            borderWidth={1}
            borderColor="$borderColor"
            backgroundColor="$gray2"
            cursor={onRowPress ? "pointer" : undefined}
            pressStyle={onRowPress ? { backgroundColor: "$color3" } : undefined}
            onPress={onRowPress ? () => onRowPress(row) : undefined}
          >
            {title && renderCell(title)}
            {cells
              .filter((cell) => cell !== title)
              .map((cell) => {
                const header = headers.get(cell.column.id);
                return (
                  <XStack
                    key={cell.id}
                    gap="$3"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                  >
                    <Text fontSize="$3" color="$gray11">
                      {header &&
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                    </Text>
                    <YStack
                      flexShrink={1}
                      alignItems="flex-end"
                      marginLeft="auto"
                    >
                      {renderCell(cell, { theme: "alt1", textAlign: "right" })}
                    </YStack>
                  </XStack>
                );
              })}
          </YStack>
        );
      })}
    </YStack>
  );
}
